import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';

function formatMoney(cents, currency = 'BRL') {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency,
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

function planMeta(slug) {
  if (slug === 'free') {
    return {
      eyebrow: 'Para começar rápido',
      tag: 'Ideal para começar',
      tagCls: 'border-[#A7E9C8] bg-[#EAFBF2] text-[#047857]',
      rail: 'bg-[#00CA72]',
      chip: 'bg-[#EAFBF2] text-[#059669]',
      check: 'text-[#059669]',
      icon: 'rocket_launch',
      cta: 'bg-[#059669] text-white hover:bg-[#047857]',
      ctaLabel: 'Começar grátis',
    };
  }
  if (slug === 'pro') {
    return {
      eyebrow: 'Para equipes em operação',
      tag: 'Mais escolhido',
      tagCls: 'bg-[#FFCC00] text-[#181B34]',
      rail: 'bg-[#6161FF]',
      chip: 'bg-[#6161FF]/10 text-[#6161FF]',
      check: 'text-[#00CA72]',
      icon: 'workspace_premium',
      cta: 'bg-[#6161FF] text-white hover:bg-[#4F4FEA]',
      ctaLabel: 'Escolher Profissional',
    };
  }
  return {
    eyebrow: 'Para fluxos de alto volume',
    tag: '',
    tagCls: '',
    rail: 'bg-slate-900',
    chip: 'bg-slate-100 text-slate-600',
    check: 'text-[#4F46E5]',
    icon: 'deployed_code',
    cta: 'border border-[#CFD2E5] bg-white text-[#181B34] hover:bg-[#F0F3FF]',
    ctaLabel: 'Falar com comercial',
  };
}

export default function Plans() {
  const [plans, setPlans] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .listPublicPlans()
      .then(setPlans)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const dimensions = useMemo(
    () => [
      { title: 'Usuários', text: 'Acesso por operador, designer e admin.' },
      { title: 'Modelos', text: 'Quantos templates de etiqueta ficam ativos.' },
      { title: 'operação', text: 'Geração, fila e impressão em um só fluxo.' },
    ],
    [],
  );

  return (
    <div className="relative min-h-screen bg-[#eef2f7]">
      <div
        aria-hidden="true"
        className="absolute inset-y-0 right-0 w-[55%] bg-[linear-gradient(180deg,#dcecff_0%,#c7e4ff_46%,#9ee4f0_100%)]"
      />
      <div className="relative mx-auto max-w-6xl px-6 py-10 lg:px-8">
        <div className="mb-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500 via-cyan-400 to-teal-300 text-white shadow-[0_16px_32px_-18px_rgba(14,165,233,0.9)]">
              <span className="material-symbols-outlined text-[18px]">sell</span>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-sky-700">EtiquetandoApp</p>
              <p className="text-[11px] text-slate-500">Estúdio de geração e impressão</p>
            </div>
          </div>
          <span className="text-[11px] font-semibold text-slate-400">Planos e preços</span>
        </div>

        <header className="mx-auto max-w-2xl text-center">
          <p className="text-[11px] font-bold text-[#6161FF]">Planos do EtiquetandoApp</p>
          <h1 className="studio-uniform-title mt-3 font-h1 text-h1 font-extrabold leading-[1.05] tracking-[-0.03em] text-[#181B34]">
            O plano certo para a sua operação de etiquetas.
          </h1>
          <p className="mt-5 text-[14px] leading-7 text-slate-600">
            Gere etiquetas em lote, imprima em impressoras térmicas e escale o acesso por usuário,
            modelo e volume de impressão — sem surpresas na cobrança.
          </p>
        </header>

        <div className="mt-10 grid gap-3 sm:grid-cols-3">
          {dimensions.map((item) => (
            <div key={item.title} className="rounded-2xl border border-slate-200 bg-white px-5 py-4 transition-all duration-300 ease-out hover:-translate-y-1 hover:border-slate-300">
              <p className="text-[11px] font-bold text-[#181B34]">{item.title}</p>
              <p className="mt-1 text-[11px] leading-6 text-slate-500">{item.text}</p>
            </div>
          ))}
        </div>

        {error && (
          <div className="mt-8 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-[11px] text-red-700">
            {error}
          </div>
        )}

        {loading ? (
          <div className="mt-12 grid gap-5 lg:grid-cols-3">
            {[1, 2, 3].map((item) => (
              <div key={item} className="h-[30rem] animate-pulse rounded-2xl border border-slate-100 bg-[#EEF0F6]" />
            ))}
          </div>
        ) : (
          <div className="mt-12 grid gap-5 lg:grid-cols-3">
            {plans.map((plan) => {
              const meta = planMeta(plan.slug);
              const isFeatured = plan.slug === 'pro';
              const priceLabel = plan.priceCents === 0 ? 'Grátis' : formatMoney(plan.priceCents, plan.currency);
              const priceUnit =
                plan.priceCents === 0
                  ? 'Sem mensalidade'
                  : `por ${plan.period === 'YEARLY' ? 'ano' : 'mês'}`;

              return (
                <article
                  key={plan.id}
                  className={`relative flex flex-col overflow-hidden rounded-2xl p-7 transition-all duration-300 ease-out ${isFeatured
                    ? 'bg-[#181B34] [clip-path:polygon(0_0,calc(100%-30px)_0,100%_30px,100%_100%,0_100%)] hover:-translate-y-1.5 hover:shadow-[0_24px_60px_-24px_rgba(97,97,255,0.45)]'
                    : 'border border-slate-200 bg-white hover:-translate-y-1.5 hover:border-slate-300 hover:shadow-[0_24px_60px_-28px_rgba(24,27,52,0.35)]'
                    }`}
                >
                  <div className={`absolute inset-x-0 top-0 h-1.5 ${meta.rail} ${isFeatured ? 'rounded-l-2xl' : 'rounded-t-2xl'}`} />

                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className={`grid h-10 w-10 place-items-center rounded-lg ${meta.chip}`}>
                        <span className="material-symbols-outlined text-[18px]">{meta.icon}</span>
                      </div>
                      <div>
                        <h2 className={`studio-uniform-title text-[1rem] font-bold ${isFeatured ? 'text-white' : 'text-[#181B34]'}`}>
                          {plan.name}
                        </h2>
                        <p className={`text-[11px] ${isFeatured ? 'text-[#9A9DB8]' : 'text-slate-500'}`}>
                          {meta.eyebrow}
                        </p>
                      </div>
                    </div>
                    {meta.tag && (
                      <span className={`rounded-full px-3 py-1 text-[10px] font-bold ${meta.tagCls}`}>
                        {meta.tag}
                      </span>
                    )}
                  </div>

                  <div className="mt-6 flex items-baseline gap-2">
                    <span className={`text-[1.9rem] font-extrabold leading-none tracking-[-0.03em] ${isFeatured ? 'text-white' : 'text-[#181B34]'}`}>
                      {priceLabel}
                    </span>
                    <span className={`text-[11px] ${isFeatured ? 'text-[#9A9DB8]' : 'text-slate-500'}`}>
                      {priceUnit}
                    </span>
                  </div>

                  <p className={`mt-4 text-[11px] leading-6 ${isFeatured ? 'text-[#C9CCDE]' : 'text-slate-600'}`}>
                    {plan.description || 'Plano disponível no catálogo.'}
                  </p>

                  <div className={`mt-6 overflow-hidden rounded-xl border text-[11px] ${isFeatured ? 'divide-y divide-white/10 border-white/10 bg-white/5' : 'divide-y divide-slate-100 border-slate-200'
                    }`}>
                    <SpecRow dark={isFeatured} label="Usuários incluídos" value={plan.maxUsers >= 100 ? 'Escala alta' : `${plan.maxUsers}`} />
                    <SpecRow dark={isFeatured} label="Modelos de etiqueta" value={plan.maxTemplates >= 1000 ? 'Escala alta' : `${plan.maxTemplates}`} />
                    <SpecRow dark={isFeatured} label="Cobrança" value={plan.period === 'YEARLY' ? 'Anual' : 'Mensal'} />
                  </div>

                  <p className={`mt-6 text-[10px] font-bold uppercase tracking-[0.08em] ${isFeatured ? 'text-[#9A9DB8]' : 'text-slate-400'}`}>
                    O que está incluso
                  </p>
                  <ul className="mt-3 space-y-2">
                    {(plan.features ?? []).map((feature) => (
                      <li key={feature} className={`flex items-start gap-2 text-[11px] leading-6 ${isFeatured ? 'text-[#C9CCDE]' : 'text-slate-700'}`}>
                        <span className={`material-symbols-outlined mt-0.5 text-[14px] ${meta.check}`}>check_circle</span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-8 flex-1" />

                  <Link
                    to={`/onboarding?plan=${plan.id}`}
                    className={`inline-flex min-h-12 items-center justify-center rounded-xl px-5 text-[11px] font-bold transition-colors ${meta.cta}`}
                  >
                    {meta.ctaLabel}
                  </Link>
                </article>
              );
            })}
          </div>
        )}

        <div className="mt-12 flex flex-col items-start justify-between gap-4 rounded-2xl border border-[#E4E6F5] bg-[#F0F3FF] px-6 py-5 lg:flex-row lg:items-center">
          <div>
            <p className="text-[11px] font-bold text-[#181B34]">Ainda prefere decidir depois?</p>
            <p className="mt-1 text-[11px] text-slate-500">Crie sua conta e deixe o catálogo visível para ajuste posterior.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/onboarding" className="inline-flex items-center rounded-xl border border-[#CFD2E5] bg-white px-5 py-3 text-[11px] font-semibold text-[#181B34] transition-colors hover:bg-[#F9FAFF]">
              Continuar sem escolher
            </Link>
            <Link to="/login" className="inline-flex items-center rounded-xl px-3 py-3 text-[11px] font-semibold text-[#4F46E5] transition-colors hover:text-[#4338CA]">
              Voltar para login
            </Link>
          </div>
        </div>

      </div>
    </div>
  );
}

function SpecRow({ dark, label, value }) {
  return (
    <div className="flex items-center justify-between px-4 py-3">
      <span className={dark ? 'text-[#9A9DB8]' : 'text-slate-500'}>{label}</span>
      <span className={`font-bold ${dark ? 'text-white' : 'text-[#181B34]'}`}>{value}</span>
    </div>
  );
}


